import { useEffect, useState } from "react";
import { mergeLines } from "../../lib/invoicePrint";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Banknote, CreditCard, Landmark, Smartphone, Gift, BookUser, Wallet, Coins } from "lucide-react";
import { formatCurrency, fromBaseCurrency, toBaseCurrency, getCurrencyConfig, API_URL } from "../../lib/utils";
import { TPASelector, useTPAs } from "../payments/TPASelector";
import { MixedPaymentEditor, emptyMixedParts, validateMixedParts, mixedPayload } from "../payments/MixedPaymentEditor";

const METHODS = [
  { id: "cash", label: "Numerário", icon: Banknote },
  { id: "card", label: "Multicaixa (TPA)", icon: CreditCard },
  { id: "appypay", label: "Multicaixa Express", icon: Smartphone },
  { id: "transfer", label: "Transferência", icon: Landmark },
  { id: "mobile", label: "Pag. Móvel", icon: Smartphone },
  { id: "giftcard", label: "Cartão Consumo", icon: Gift },
  { id: "credit", label: "Crédito (Conta)", icon: BookUser },
  { id: "mixed", label: "Misto", icon: Coins },
];

const inputCls = "w-full h-9 rounded-md bg-muted border border-border px-2.5 text-sm focus:outline-none focus:border-emerald-500";

export const POSPayModal = ({ open, onClose, target, onConfirm, processing, token, initialCustomer }) => {
  const [method, setMethod] = useState("cash");
  const [customerName, setCustomerName] = useState("");
  const [customerNif, setCustomerNif] = useState("");
  const [amountReceived, setAmountReceived] = useState("");
  const [giftCode, setGiftCode] = useState("");
  const [giftInfo, setGiftInfo] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [creditCustomerId, setCreditCustomerId] = useState("");
  const [phone, setPhone] = useState("");
  const [tpaId, setTpaId] = useState("");
  const [mixedParts, setMixedParts] = useState([]);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const tpas = useTPAs(headers, open);

  useEffect(() => {
    if (open) {
      setMethod("cash");
      setAmountReceived("");
      setGiftCode("");
      setGiftInfo(null);
      setCreditCustomerId("");
      setPhone("");
      setTpaId("");
      setMixedParts([]);
      setCustomerName(initialCustomer?.name || "");
      setCustomerNif(initialCustomer?.nif || "");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tpas.length === 1) setTpaId(tpas[0].tpa_id);
  }, [tpas]);

  useEffect(() => {
    if (method === "mixed" && target && !mixedParts.length) setMixedParts(emptyMixedParts(target.total || 0, tpas));
  }, [method]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (method === "credit" && customers === null) {
      axios
        .get(`${API_URL}/customers`, { headers, withCredentials: true })
        .then((r) => setCustomers(r.data))
        .catch(() => setCustomers([]));
    }
  }, [method]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!target) return null;
  const total = target.total || 0;
  const displayTotal = fromBaseCurrency(total);
  const steps = getCurrencyConfig().currency === "AOA" ? [500, 1000, 2000, 5000, 10000] : [1, 5, 10, 50];
  const quick = [...new Set([displayTotal, ...steps.map((s) => Math.ceil(displayTotal / s) * s)])].filter((v) => v >= displayTotal).slice(0, 5);
  const receivedBase = amountReceived ? toBaseCurrency(amountReceived) : null;
  const change = receivedBase !== null ? receivedBase - total : null;

  const checkGift = async () => {
    if (!giftCode.trim()) return;
    try {
      const r = await axios.get(`${API_URL}/gift-cards/lookup?code=${encodeURIComponent(giftCode.trim())}`, { headers, withCredentials: true });
      setGiftInfo(r.data);
    } catch (e) {
      setGiftInfo(null);
      toast.error(e.response?.data?.detail || "Cartão não encontrado");
    }
  };

  const confirm = () => {
    if (method === "cash" && receivedBase !== null && receivedBase < total) return toast.error("Valor recebido inferior ao total");
    if (method === "giftcard" && !giftCode.trim()) return toast.error("Indique o código do cartão");
    if (method === "credit" && !creditCustomerId) return toast.error("Selecione o cliente da conta corrente");
    if (method === "appypay" && phone.replace(/\D/g, "").length < 9) return toast.error("Indique o telemóvel do cliente (9 dígitos)");
    if (method === "card" && tpas.length && !tpaId) return toast.error("Selecione o TPA onde o cartão foi passado");
    if (method === "mixed") {
      const err = validateMixedParts(mixedParts, total, tpas);
      if (err) return toast.error(err);
    }
    onConfirm({ method, customerName: customerName.trim(), customerNif: customerNif.trim(), amountReceived: method === "cash" ? receivedBase : null, giftCardCode: giftCode.trim(), creditCustomerId, phone: phone.trim(), tpaId: method === "card" ? tpaId : "", mixed: method === "mixed" ? mixedPayload(mixedParts) : null });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !processing && onClose()}>
      <DialogContent className="max-w-lg" data-testid="pos-pay-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-emerald-500" /> {target.title || "Fechar conta & Faturar"}
          </DialogTitle>
          <DialogDescription>{target.subtitle || "A fatura é emitida automaticamente após o pagamento."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg bg-muted p-3 max-h-32 overflow-y-auto space-y-0.5 text-sm">
            {mergeLines(target.items).map((it, i) => (
              <div key={i} className="flex justify-between gap-2">
                <span className="truncate">
                  {it.quantity}x {it.name}
                </span>
                <span className="font-mono shrink-0">{formatCurrency(it.total)}</span>
              </div>
            ))}
            <div className="flex justify-between font-bold text-base pt-2 mt-1 border-t border-border">
              <span>Total a pagar</span>
              <span className="font-mono text-emerald-500" data-testid="pos-pay-total">{formatCurrency(total)}</span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {METHODS.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setMethod(m.id)}
                className={`h-10 rounded-md border text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                  method === m.id ? "bg-emerald-600 border-emerald-500 text-white" : "bg-muted border-border hover:border-emerald-500/60"
                }`}
                data-testid={`pos-pay-method-${m.id}`}
              >
                <m.icon className="w-3.5 h-3.5" /> {m.label}
              </button>
            ))}
          </div>

          {method === "cash" && (
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground">Valor recebido (para troco)</label>
              <input type="number" min="0" step="0.01" className={`${inputCls} font-mono`} value={amountReceived} onChange={(e) => setAmountReceived(e.target.value)} placeholder={displayTotal.toFixed(2)} data-testid="pos-amount-received" />
              <div className="flex flex-wrap gap-1.5">
                {quick.map((v) => (
                  <button key={v} type="button" onClick={() => setAmountReceived(String(v))} className="h-7 px-2.5 rounded-md bg-muted border border-border text-xs font-mono hover:border-emerald-500/60" data-testid={`pos-quick-amount-${v}`}>
                    {v.toLocaleString("pt-AO")}
                  </button>
                ))}
              </div>
              {change !== null && change >= 0 && (
                <p className="text-sm font-semibold text-emerald-500" data-testid="pos-change-preview">Troco: {formatCurrency(change)}</p>
              )}
              {change !== null && change < 0 && <p className="text-sm text-red-500">Valor insuficiente</p>}
            </div>
          )}

          {method === "card" && <TPASelector tpas={tpas} value={tpaId} onChange={setTpaId} dark />}

          {method === "mixed" && mixedParts.length > 0 && <MixedPaymentEditor parts={mixedParts} onChange={setMixedParts} total={total} tpas={tpas} dark />}

          {method === "giftcard" && (
            <div className="space-y-2" data-testid="giftcard-payment-section">
              <div className="flex gap-2">
                <input className={`${inputCls} font-mono uppercase`} value={giftCode} onChange={(e) => setGiftCode(e.target.value.toUpperCase())} placeholder="LUM-XXXX-XXXX" data-testid="giftcard-code-input" />
                <Button type="button" variant="outline" onClick={checkGift} data-testid="giftcard-check-btn">Verificar</Button>
              </div>
              {giftInfo && (
                <p className={`text-sm ${giftInfo.balance >= total ? "text-emerald-500" : "text-red-500"}`} data-testid="giftcard-balance-info">
                  Saldo: {formatCurrency(giftInfo.balance)} · {giftInfo.status === "active" ? "ativo" : giftInfo.status}
                </p>
              )}
            </div>
          )}

          {method === "appypay" && (
            <div className="space-y-1" data-testid="appypay-payment-section">
              <label className="text-xs text-muted-foreground">Telemóvel do cliente (Multicaixa Express) — o cliente aprova na app e a fatura é emitida automaticamente</label>
              <input className={`${inputCls} font-mono text-base`} value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d ]/g, ""))} placeholder="923 000 000" inputMode="numeric" autoFocus data-testid="appypay-phone-input" />
            </div>
          )}

          {method === "credit" && (
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Cliente (conta corrente) — emite Fatura FT a crédito</label>
              <select className={inputCls} value={creditCustomerId} onChange={(e) => setCreditCustomerId(e.target.value)} data-testid="credit-customer-select">
                <option value="">Selecionar cliente…</option>
                {(customers || []).map((c) => (
                  <option key={c.customer_id} value={c.customer_id}>
                    {c.name}
                    {c.nif ? ` · ${c.nif}` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          {method !== "credit" && (
            <div className="grid grid-cols-2 gap-2">
              <input className={inputCls} value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Nome na fatura (opcional)" data-testid="pos-pay-customer-name" />
              <input className={`${inputCls} font-mono`} value={customerNif} onChange={(e) => setCustomerNif(e.target.value)} placeholder="NIF (opcional)" data-testid="pos-pay-customer-nif" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={processing}>Cancelar</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-500 text-white gap-2" onClick={confirm} disabled={processing} data-testid="pos-confirm-pay-btn">
            {processing ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : method === "appypay" ? <Smartphone className="w-4 h-4" /> : <Wallet className="w-4 h-4" />}
            {method === "appypay" ? "Enviar pedido de pagamento" : "Confirmar & Emitir Fatura"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
